import React, { useState, useRef, useEffect } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Box, Popper, Button, Typography, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { calendar_icn } from "../../assets/images/images"; // Adjust the path as necessary
import { useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";

  // --- Year Selector ---
  const YearSelector = ({ selectedYear, onSelectYear, onBack }) => {
    const [startYear, setStartYear] = useState(selectedYear - 3);
    const currentYear = dayjs().year(); // Get the current year
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);
        const isMobile = useMediaQuery("(max-width:600px)");

    const handlePrev = () => setStartYear((prev) => prev - 12);
    const handleNext = () => setStartYear((prev) => prev + 12);

    return (
      <Box sx={{ width: 260, px: 1, pb: 1 }} className="year-selector">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <div className="selector-header">
            <Typography variant="subtitle1" fontWeight="bold">
              {years[0]} - {years[years.length - 1]}
            </Typography>

            {onBack && (
              <IconButton size="small" onClick={onBack}>
                <i className="fa-solid fa-arrow-right"></i>
              </IconButton>
            )}
          </div>
          <Box>
            <IconButton size="small" onClick={handlePrev}>
              <i className="fas fa-chevron-left"></i>
            </IconButton>
            <IconButton size="small" onClick={handleNext}>
              <i className="fas fa-chevron-right"></i>
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}
        >
          {years.map((year) => {
            const isSelected = year === selectedYear;
            const isDisabled = year > currentYear; // Disable future years
            return (
              <Button
                key={year}
                variant={isSelected ? "contained" : "text"}
                size="small"
                onClick={() => onSelectYear(year)}
                disabled={isDisabled}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  boxShadow: "none",
                  color: isSelected ? "#fff" : "text.primary",
                  bgcolor: isSelected ? "#4493EF" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "#3a7fd1" : "#f0f0f0",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    color: "#c4c4c4", // plain faded text, no box
                    backgroundColor: "transparent",
                    border: "none",
                  },
                }}
              >
                {year}
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  };

  // --- Month Selector ---
  const MonthSelector = ({
    selectedMonth,
    onSelectMonth,
    selectedYear,
    onBack,
  }) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Get current month and year using dayjs
    const currentMonth = dayjs().month(); // Current month (0-11)
    const currentYear = dayjs().year(); // Current year

    return (
      <Box sx={{ width: 260, px: 1, pb: 1 }} className="month-selector">
        <div className="selector-header">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Select Month
          </Typography>
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <i className="fa-solid fa-arrow-right"></i>
            </IconButton>
          )}
        </div>
        <Box
          sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}
        >
          {monthNames.map((name, index) => {
            // Disable months in the future based on selected year and current month
            const isDisabled =
              (selectedYear === currentYear && index > currentMonth) || // Disable months after the current month in the selected year
              selectedYear > currentYear; // Disable all months for years greater than the current year
            const isSelected = index === selectedMonth;
            return (
              <Button
                key={index}
                variant={isSelected ? "contained" : "text"}
                size="small"
                onClick={() => !isDisabled && onSelectMonth(index)}
                disabled={isDisabled} // Disable button if month is in the future
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  boxShadow: "none",
                  color: isSelected ? "#fff" : "text.primary",
                  bgcolor: isSelected ? "#4493EF" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "#3a7fd1" : "#f0f0f0",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    color: "#c4c4c4", // plain faded text, no box
                    backgroundColor: "transparent",
                    border: "none",
                  },
                }}
              >
                {name.substring(0, 3)}
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  };

  // --- Day Selector with Range Highlight ---
  const DaySelector = ({
    selectedDate,
    range,
    onSelectDay,
    onBack,
    selectingStart,
  }) => {
    const daysInMonth = selectedDate.daysInMonth();
    const currentMonth = selectedDate.month();
    const currentYear = selectedDate.year();

    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    // Get the first day of the month (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const firstDayOfMonth = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .startOf("month")
      .day();

    // Generate empty cells for days before the first day of the month
    const emptyCells = Array(firstDayOfMonth).fill(null);

    // Weekday abbreviations
    const dayNames = ["S", "M", "T", "W", "T", "F", "S"];

    const getDate = (day) =>
      dayjs().year(currentYear).month(currentMonth).date(day);

    const isStart = (day) =>
      range.start && getDate(day).isSame(range.start, "day");
    const isEnd = (day) => range.end && getDate(day).isSame(range.end, "day");

    const isInRange = (day) => {
      const date = getDate(day);
      if (range.start && range.end) {
        return date.isAfter(range.start) && date.isBefore(range.end);
      }
      return false;
    };
    // Compare if the day is in the future
    const isDisabledForSelection = (day) => {
      const date = getDate(day);
      // Disable future dates
      if (date.isAfter(dayjs(), "day")) return true;

      // If selecting end date, disable dates before start
      if (!selectingStart && range.start && date.isBefore(range.start, "day")) {
        return true;
      }

      return false;
    };

    return (
      <Box sx={{ width: 260, px: 1, pb: 1 }} className="day-selector">
        <div className="selector-header">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Select Range
          </Typography>
          {onBack && (
            <IconButton size="small" onClick={onBack}>
              <i className="fa-solid fa-arrow-right"></i>
            </IconButton>
          )}
        </div>

        {/* Day names header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            textAlign: "center",
            fontWeight: "bold",
            marginBottom: "4px",
            userSelect: "none",
            color: "text.secondary",
          }}
        >
          {dayNames.map((dayName, idx) => (
            <Box key={idx}>{dayName}</Box>
          ))}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "4px" }}>
          {/* Render empty cells before the first day of the month */}
        {emptyCells.map((_, idx) => (
          <Box key={`empty-${idx}`} />
        ))}

    {days.map((day) => {
      const start = isStart(day);
      const end = isEnd(day);
      const inRange = isInRange(day);
      const isDisabled = isDisabledForSelection(day); // Check if the day is in the future
      const isSelected = start || end;
      const hasBothDates = range.start && range.end;
      const sameDay = hasBothDates && range.start.isSame(range.end, "day");

      // The light strip behind: full width for in-range days,
      // half width (inward side) behind the start/end circles
        const stripSx =
        hasBothDates && !sameDay
          ? inRange
            ? { left: 0, right: 0 }            // full band for middle days
            : start
            ? { left: "50%", right: 0 }        // right half behind start circle
            : end
            ? { left: 0, right: "50%" }        // left half behind end circle
            : null
          : null;

      return (
        <Box
          key={day}
          sx={{ position: "relative", display: "flex", justifyContent: "center" }}
        >
          {/* light-blue band layer */}
          {stripSx && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                bgcolor: "#82B7F4",
                borderTopLeftRadius: start ? "50%" : 0,
                borderBottomLeftRadius: start ? "50%" : 0,
                borderTopRightRadius: end ? 0 : 0,
                borderBottomRightRadius: end ? 0 : 0,
                ...stripSx,
              }}
            />
          )}

          {/* the day button on top */}
          <Button
            size="small"
            onClick={() => !isDisabled && onSelectDay(day)} // Prevent selection of future dates
            disabled={isDisabled}
            disableRipple
            sx={{
              minWidth: 0,
              width: 32,
              height: 32,
              p: 0,
              position: "relative",   // sits above the band
              zIndex: 1,
              borderRadius: "50%",
              bgcolor: isSelected ? "#4493EF" : "transparent",
              color: isSelected
                ? "#fff"
                : inRange
                ? "#fff"
                : isDisabled
                ? "#c4c4c4"
                : "inherit",
              "&:hover": {
                backgroundColor: isSelected
                  ? "#3a7fd1"
                  : inRange
                  ? "transparent"
                  : "#f0f0f0",
              },
              "&.Mui-disabled": {
                color: "#c4c4c4",
                backgroundColor: isSelected ? "#4493EF" : "transparent",
              },
            }}
          >
            {day}
          </Button>
        </Box>
      );
    })}
  </Box>
      </Box>
    );
  };

  // --- Main CalendarView Component ---
  const CalendarView = ({
    defaultView = "year",
    periodTabTwo,
    onPeriodChange,
    onYearChange,
    onMonthChange,
    onstartdatechange,
    onenddatechange,
    onmonth_yearchnage,
    setdot,
    isAIGenerating,
    externalYear,
    externalMonth,
    externalMonthYear,
    externalStartDate,
    externalEndDate,
  }) => {
    const calendarRef = useRef(null); // Ref to detect clicks outside the calendar
    const today = dayjs();
    const currentYear = today.year();
    const currentMonth = today.month();
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedYearState, setSelectedYearState] = useState(currentYear);

    const [range, setRange] = useState({
      start: dayjs().subtract(7, "day"),
      end: dayjs(),
    });

    const [open, setOpen] = useState(false);
    const [view, setView] = useState("year"); // Always start with year view
    const anchorRef = useRef(null);

    const [selectingStart, setSelectingStart] = useState(true); // true = start date, false = end date
    const [isStartSelected, setIsStartSelected] = useState(false); // Track if start date is selected
    const [placement, setPlacement] = useState("bottom-start");

    useEffect(() => {
      const startDate = range.start.format("DD-MM-YYYY");
      const endDate = range.end.format("DD-MM-YYYY");

      onstartdatechange(startDate);
      onenddatechange(endDate);
    }, []);

    useEffect(() => {
      if (externalYear) {
        setSelectedYearState(parseInt(externalYear));
        setSelectedDate(dayjs().year(parseInt(externalYear)));
      }
    }, [externalYear]);

    useEffect(() => {
      if (externalMonth) {
        const monthNum = parseInt(externalMonth) - 1; // dayjs months are 0-based
        setSelectedDate(dayjs().month(monthNum).year(selectedYearState));
      }
    }, [externalMonth, selectedYearState]);

    useEffect(() => {
      if (externalStartDate && externalEndDate) {
        const start = dayjs(externalStartDate, "DD-MM-YYYY");
        const end = dayjs(externalEndDate, "DD-MM-YYYY");
        if (start.isValid() && end.isValid()) {
          setRange({ start, end });
        }
      }
    }, [externalStartDate, externalEndDate]);

    // Reset calendar view when periodTab changes
    useEffect(() => {
      if (periodTabTwo === "year") {
        // For year tab, always show year view
        setView("year");
        // Reset to current year if external year is provided
        if (externalYear) {
          const yearNum = parseInt(externalYear);
          setSelectedYearState(yearNum);
          setSelectedDate(dayjs().year(yearNum));
        }
      } else if (periodTabTwo === "month" || periodTabTwo === "week") {
        // For month/week tabs, show month view
        setView("month");
      } else if (periodTabTwo === "custom") {
        // For custom tab, show day view for date range selection
        setView("day");
      }
    }, [periodTabTwo, externalYear]);

    useEffect(() => {
      if (view === "year") {
        setPlacement("bottom-start");
      } else if (view === "month") {
        setPlacement("bottom-start");
      } else if (view === "day") {
        setPlacement("bottom-start");
      }
    }, [view]);

    // Close the calendar when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          setOpen(false); // Close the calendar
        }
      };

      // Add event listener for clicks outside
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup event listener on component unmount
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    // Handle toggle and state updates for calendar view
    // const handleToggle = (whichDate = "start") => {
    //   // Prevent calendar interactions during AI generation
    //   if (isAIGenerating) {
    //     return;
    //   }

    //   setOpen((prev) => {
    //     const newState = !prev;
    //     if (newState) {
    //       // Always start with year view for all tabs
    //       setView("year");
    //       setSelectingStart(whichDate === "start");
    //     }
    //     return newState;
    //   });
    // };
     //bb
  const [anchorEl, setAnchorEl] = useState(null);
  const handleToggle = (whichDate = "start") => (event) => {
  if (isAIGenerating) return;
  setAnchorEl(event.currentTarget); // anchor to whichever button was tapped
  setOpen((prev) => {
    const newState = !prev;
    if (newState) {
      setView("year");
      setSelectingStart(whichDate === "start");
    }
    return newState;
  });
};

    const handleYearSelect = (year) => {
      // Prevent calendar interactions during AI generation
      if (isAIGenerating) {
        return;
      }

      setSelectedYearState(year); // <-- update the selected year

      setSelectedDate((prev) => prev.year(year));

      // Clear any previously clicked dot data when making calendar selection
      if (setdot) {
        setdot({});
      }

      if (periodTabTwo === "custom") {
        // For custom tab, allow month selection after year
        setView("month");
      } else if (periodTabTwo === "year") {
        // For yearly tab, don't allow month selection, close calendar immediately
        setOpen(false);
        // Ensure year is a number
        const yearNumber = parseInt(year, 10);
        if (!isNaN(yearNumber)) {
          onPeriodChange("year", yearNumber); // Trigger graph update for selected year
          onYearChange(yearNumber);
          // Update month_year for API - create the correct format from the selected year
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const currentMonth = selectedDate.month(); // Get current month (0-11)
          const correctMonthYear = `${monthNames[currentMonth]}-${yearNumber}`;
          onmonth_yearchnage(correctMonthYear);
        }
      } else if (periodTabTwo === "week" || periodTabTwo === "month") {
        // For weekly and monthly tabs, allow month selection
        setView("month");
      } else {
        // For any other case, allow month selection
        setView("month");
      }
    };

      const handleMonthSelect = (month) => {
        // Prevent calendar interactions during AI generation
        if (isAIGenerating) {
          return;
      }

      setSelectedDate((prev) => prev.month(month));

      // Clear any previously clicked dot data when making calendar selection
      if (setdot) {
        setdot({});
      }

      if (periodTabTwo === "custom") {
        setView("day");
      } else if (periodTabTwo === "year") {
        // For yearly tab, don't allow month selection, close calendar
        setOpen(false);
        // Ensure year is a number
        const yearNumber = parseInt(selectedDate.year(), 10);
        if (!isNaN(yearNumber)) {
          onPeriodChange("year", yearNumber); // Keep the year context
        }
      } else {
        // For weekly and monthly tabs, close calendar after month selection
        setOpen(false);
        // Ensure month is a number and convert to 1-based month (1-12) for display
        const monthNumber = parseInt(month, 10);
        if (!isNaN(monthNumber)) {
          onPeriodChange("Monthly", monthNumber); // Trigger graph update for selected month
          onMonthChange(monthNumber + 1);
          // Update month_year for API - create the correct format from the selected month and year
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const selectedYear = selectedYearState; // Use selectedYearState instead of selectedDate.year()
          const correctMonthYear = `${monthNames[monthNumber]}-${selectedYear}`;
          onmonth_yearchnage(correctMonthYear);
        }
      }
    };

    const handleDaySelect = (day) => {
      const newDate = selectedDate.date(day);
      setIsStartSelected(true); // Enable second button for selecting end manually

      // Clear any previously clicked dot data when making calendar selection
      if (setdot) {
        setdot({});
      }

      if (selectingStart) {
        // Selecting start date
        // If existing end is before new start, reset it
        if (range.end && newDate.isAfter(range.end)) {
          setRange({ start: newDate, end: null }); // Clear end if new start is after current end
        } else {
          setRange({ start: newDate, end: range.end }); // Preserve the end date if it's still valid
        }
        setSelectedDate(newDate);
        setOpen(false); // Close calendar after selecting start date
        setIsStartSelected(true); // Enable second button for selecting end manually
        onstartdatechange(newDate.format("DD-MM-YYYY"));
        // Update month_year for API
        onmonth_yearchnage(newDate.format("MMM-YYYY"));
      } else {
        // Selecting end date manually
        if (newDate.isBefore(range.start)) {
          // If new end date is before start date, reset and set new start
          setRange({ start: newDate, end: newDate.add(21, "day") });
          setSelectedDate(newDate);
          setSelectingStart(true);
          setIsStartSelected(false);
          setOpen(false); // Close calendar after resetting
          // Update month_year for API
          onmonth_yearchnage(newDate.format("MMM-YYYY"));
        } else {
          // Valid end date selection
          setRange({ ...range, end: newDate });
          setSelectedDate(newDate);
          setOpen(false); // Close calendar after selecting end date
          onPeriodChange("custom", { start: range.start, end: newDate }); // Fire only when both dates are selected manually

          // Get start and end dates as DD-MM-YYYY format
          const startDate = range.start.format("DD-MM-YYYY");
          const endDate = newDate.format("DD-MM-YYYY");

          onenddatechange(endDate);
          onstartdatechange(startDate);
        }
      }
    };

    const handleCustomSelect = (custom) => {
      setSelectedDate((prev) => prev.year(custom));
      if (periodTabTwo === "year") {
        // For yearly tab, don't allow month selection
        setOpen(false);
        onPeriodChange("year", custom);
      } else {
        setView("month");
        // Ensure custom is a number for year selection
        const yearNumber = parseInt(custom, 10);
        if (!isNaN(yearNumber)) {
          onPeriodChange("month", yearNumber);
        }
      }
    };

    const displayText =
      periodTabTwo === "custom"
        ? `${range.start.format("DD MMM YYYY")}`
        : periodTabTwo === "week" || periodTabTwo === "month"
        ? selectedDate.format("MMM-YYYY")
        : selectedDate.format("YYYY");

    // const popperOptions = {
    //   modifiers: [
    //     {
    //       name: "preventOverflow",
    //       options: {
    //         boundary: "viewport",
    //       },
    //     },
    //   ],
    // };

    //bb
    const popperOptions = {
  modifiers: [
    { name: "offset", options: { offset: [0, 8] } },
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        padding: 8,
        altAxis: true, // shift horizontally instead of jumping away
      },
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["top-start", "bottom-end"], // only flip vertically, stay attached
      },
    },
  ],
};

    const role = useSelector((state) => state?.globalState?.role || "");

    // Shared calendar body to avoid duplicating the Popper contents
    const calendarBody = (
      <Box
        sx={{
          p: 1,
          backgroundColor: "#fff",
          boxShadow: 3,
          borderRadius: 2,
        }}
        ref={calendarRef}
      >
        {view === "year" && (
          <YearSelector
            selectedYear={selectedDate.year()}
            onSelectYear={handleYearSelect}
          />
        )}
        {view === "month" && (
          <MonthSelector
            selectedMonth={selectedDate.month()}
            selectedYear={selectedYearState}
            onSelectMonth={handleMonthSelect}
          />
        )}
        {view === "day" && (
          <DaySelector
            selectedDate={selectedDate}
            range={range}
            onSelectDay={handleDaySelect}
            selectingStart={selectingStart}
          />
        )}
      </Box>
    );

    return (
      <div
        style={{
          opacity: isAIGenerating ? 0.5 : 1,
          pointerEvents: isAIGenerating ? "none" : "auto",
        }}
      >
        <Box className="calender-view">
          <div className="calender-button">
            <Button
              ref={anchorRef}
              // onClick={handleToggle}
              // onClick={() => handleToggle("start")}
                            onClick={handleToggle("start")}

              variant="outlined"
              className="calendar-button1"
              sx={{
                borderRadius: "25px",
                padding: "6px 12px",
                borderColor: "#e0e0e0",
                backgroundColor: "#fff",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: "100px",
              }}
              // disabled={isStartSelected && !range.end} // Disable second button until start date is selected
            >
              <img
                src={calendar_icn}
                alt="calendar"
                style={{ width: "16px", height: "16px" }}
              />
              <Typography color="textPrimary">{displayText}</Typography>
            </Button>

            {/* Second button when Custom range is selected */}
            {periodTabTwo === "custom" && (
              <>
                <span>-</span>
                <Button
                  ref={anchorRef}
                  onClick={ handleToggle("end")}
                  variant="outlined"
                  className="calendar-button2"
                  sx={{
                    borderRadius: "25px",
                    padding: "6px 12px",
                    borderColor: "#e0e0e0",
                    backgroundColor: "#fff",
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: "100px",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <img
                    src={calendar_icn}
                    alt="calendar"
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Typography color="textPrimary">
                    {range.end
                      ? range.end.format("DD MMM YYYY")
                      : dayjs().format("DD MMM YYYY")}
                  </Typography>
                </Button>
              </>
            )}

            <div className="calender-icon">
           <Popper
  key={view}
  open={open}
  anchorEl={anchorEl}
  // disablePortal={isMobile}   // keeps popper in DOM flow next to the button on mobile
  placement={placement}
  modifiers={popperOptions.modifiers}
  sx={{ zIndex: 1300 }}      // needed since disablePortal loses body-level stacking
  className={
    role === "Teacher"
      ? "calendar-popper"
      : "calendar-popper student-calendar-popper"
  }
>
  {calendarBody}
</Popper>
            </div>
          </div>
        </Box>
      </div>
    );
  };

export default CalendarView;